import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import ProcessStatusContext from "../../context/ProcessStatusContext";
import { useAuthInfo } from "../../context/AuthInfoContext";
import { useToken } from "hooks/useToken";
import { getProcessStatus } from "./services/overviewService";
import { banking } from "utils/api/generated/manucoApi";

// Haalt de kwartaalvoortgang op en deelt die met dashboard + sidebar-badges.
// Faalt stil (badges/banner verdwijnen dan gewoon).
//
// Eerste lading (#79): op het Overzicht ("/") meteen, want daar is process-status de hoofdinhoud. Op een andere pagina
// pas na INITIAL_DELAY_MS: dan voedt hij enkel de badges, en krijgen de eigen requests van die pagina voorrang op de
// (onder last 2-4 s dure) process-status. Latere verversingen (refresh) gaan altijd meteen.
export const INITIAL_DELAY_MS = 1500;

export default function ProcessStatusProvider({ children }) {
  const { pathname } = useLocation();
  // Enkel de route bij de eerste lading telt; navigeren daarna verandert niets aan het ophalen.
  const initialPathRef = useRef(pathname);
  const isAuthenticated = useIsAuthenticated();
  const { loading: authLoading, allowed, pages } = useAuthInfo();
  // Open profielvoorstellen (badge Bankprofielen): enkel voor wie die pagina ziet (admin).
  const seesProfiles = Array.isArray(pages) && pages.includes("bankProfiles");
  const [openProposals, setOpenProposals] = useState(0);
  const tokenProvider = useToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || authLoading || !allowed) return;
    setLoading(true);
    try {
      const data = await getProcessStatus(tokenProvider);
      if (!mounted.current) return;
      setStatus(data);
      setError(null);
    } catch (e) {
      if (!mounted.current) return;
      setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [isAuthenticated, authLoading, allowed, tokenProvider]);

  const refreshProposals = useCallback(async () => {
    if (!isAuthenticated || authLoading || !allowed || !seesProfiles) return;
    try {
      const list = await banking.profileProposalList(tokenProvider, { status: "PROPOSED" }, { retries: 0 });
      if (mounted.current) setOpenProposals(Array.isArray(list) ? list.length : 0);
    } catch {
      // Stil: zonder badge werkt de sidebar gewoon.
    }
  }, [isAuthenticated, authLoading, allowed, seesProfiles, tokenProvider]);

  // Eerste lading (pas als de aanmelding rond is) eventueel uitgesteld; daarna (bv. gewijzigde rechten) meteen.
  const ready = isAuthenticated && !authLoading && allowed;
  const startedRef = useRef(false);
  const startDeferred = useCallback((fn) => {
    if (startedRef.current || initialPathRef.current === "/") {
      startedRef.current = true;
      fn();
      return undefined;
    }
    const timer = setTimeout(() => {
      startedRef.current = true;
      fn();
    }, INITIAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => (ready ? startDeferred(refresh) : undefined), [ready, startDeferred, refresh]);
  useEffect(() => (ready ? startDeferred(refreshProposals) : undefined), [ready, startDeferred, refreshProposals]);

  const refreshAll = useCallback(() => {
    refresh();
    refreshProposals();
  }, [refresh, refreshProposals]);

  const value = useMemo(() => {
    const totals = status?.totals || {};
    const badges = {
      importPending: totals.importPending > 0 ? String(totals.importPending) : null,
      processingPending: totals.processingPending > 0 ? String(totals.processingPending) : null,
      bankProfileProposals: openProposals > 0 ? String(openProposals) : null,
      // "Te doen": open werklijstitems. process-status levert worklist enkel voor invest/admin
      // en fail-soft null zolang de DB de proc mist: dan geen badge.
      worklistOpen: status?.worklist?.openTotal > 0 ? String(status.worklist.openTotal) : null,
    };
    return { status, loading, error, badges, refresh: refreshAll };
  }, [status, loading, error, refreshAll, openProposals]);

  return <ProcessStatusContext.Provider value={value}>{children}</ProcessStatusContext.Provider>;
}
